/**
 * EditorDocument — the single owner of the project state and its undo/redo history.
 *
 * State is immutable (structural sharing via immer). Every edit goes through `apply`, which records
 * forward/inverse patches. Continuous gestures (dragging a clip, scrubbing a value) are grouped into
 * one undo step with `begin`/`commit` (or by passing the same `coalesceKey`), so the history stays
 * meaningful without storing a snapshot per mouse move.
 */
import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import type { Project } from '../model/schema.ts';

enablePatches();

export interface HistoryEntry {
  label: string;
  patches: Patch[];
  inverse: Patch[];
  coalesceKey?: string;
  timestamp: number;
}

export interface ApplyOptions {
  /** Consecutive applies with the same key (outside a transaction) merge into one undo step. */
  coalesceKey?: string;
  /** Skip recording (e.g. for non-undoable metadata updates). */
  record?: boolean;
}

export type Recipe<R> = (draft: Project) => R;
export type Listener = (project: Project, reason: ChangeReason) => void;
export type ChangeReason = 'edit' | 'undo' | 'redo' | 'load' | 'rollback';

export class EditorDocument {
  private state: Project;
  private past: HistoryEntry[] = [];
  private future: HistoryEntry[] = [];
  private listeners = new Set<Listener>();
  private txn: { label: string; patches: Patch[]; inverse: Patch[]; base: Project } | null = null;
  private revision = 0;
  private savedRevision = 0;

  constructor(
    project: Project,
    private readonly maxHistory = 500,
  ) {
    this.state = project;
  }

  get project(): Project {
    return this.state;
  }

  /** Monotonic counter, increments on every change; useful for dirty tracking. */
  get version(): number {
    return this.revision;
  }

  get isDirty(): boolean {
    return this.revision !== this.savedRevision;
  }

  markSaved(revision = this.revision): void {
    this.savedRevision = revision;
  }

  get canUndo(): boolean {
    return this.past.length > 0 && !this.txn;
  }

  get canRedo(): boolean {
    return this.future.length > 0 && !this.txn;
  }

  get undoLabel(): string | undefined {
    return this.past[this.past.length - 1]?.label;
  }

  get redoLabel(): string | undefined {
    return this.future[this.future.length - 1]?.label;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Replaces the whole document (e.g. after loading) and clears history. */
  load(project: Project): void {
    this.state = project;
    this.past = [];
    this.future = [];
    this.txn = null;
    this.revision++;
    this.savedRevision = this.revision;
    this.emit('load');
  }

  /**
   * Applies a recipe atomically. If the recipe throws, nothing changes and the error propagates.
   * Returns the recipe's return value.
   */
  apply<R>(label: string, recipe: Recipe<R>, options: ApplyOptions = {}): R {
    let result!: R;
    const [next, patches, inverse] = produceWithPatches(this.state, (draft) => {
      result = recipe(draft as Project);
    });
    if (patches.length === 0) return result;
    this.state = next;
    this.revision++;
    if (options.record !== false) this.record(label, patches, inverse, options.coalesceKey);
    this.emit('edit');
    return result;
  }

  /** Starts a transaction: subsequent applies merge into a single undo step until `commit`. */
  begin(label: string): void {
    if (this.txn) this.commit();
    this.txn = { label, patches: [], inverse: [], base: this.state };
  }

  commit(): void {
    const txn = this.txn;
    if (!txn) return;
    this.txn = null;
    if (txn.patches.length > 0)
      this.pushEntry({ label: txn.label, patches: txn.patches, inverse: txn.inverse, timestamp: Date.now() });
  }

  /** Aborts a transaction and restores the state from before `begin`. */
  rollback(): void {
    const txn = this.txn;
    if (!txn) return;
    this.txn = null;
    if (this.state !== txn.base) {
      this.state = txn.base;
      this.revision++;
      this.emit('rollback');
    }
  }

  get inTransaction(): boolean {
    return this.txn !== null;
  }

  undo(): boolean {
    if (this.txn) this.commit();
    const entry = this.past.pop();
    if (!entry) return false;
    this.state = applyPatches(this.state, entry.inverse);
    this.future.push(entry);
    this.revision++;
    this.emit('undo');
    return true;
  }

  redo(): boolean {
    if (this.txn) this.commit();
    const entry = this.future.pop();
    if (!entry) return false;
    this.state = applyPatches(this.state, entry.patches);
    this.past.push(entry);
    this.revision++;
    this.emit('redo');
    return true;
  }

  get history(): ReadonlyArray<Pick<HistoryEntry, 'label' | 'timestamp'>> {
    return this.past;
  }

  private record(label: string, patches: Patch[], inverse: Patch[], coalesceKey?: string): void {
    if (this.txn) {
      this.txn.patches.push(...patches);
      this.txn.inverse.unshift(...inverse);
      return;
    }
    const last = this.past[this.past.length - 1];
    if (coalesceKey && last?.coalesceKey === coalesceKey && this.future.length === 0) {
      last.patches.push(...patches);
      last.inverse.unshift(...inverse);
      last.timestamp = Date.now();
      return;
    }
    this.pushEntry({ label, patches, inverse, coalesceKey, timestamp: Date.now() });
  }

  private pushEntry(entry: HistoryEntry): void {
    this.past.push(entry);
    if (this.past.length > this.maxHistory) this.past.shift();
    this.future = [];
  }

  private emit(reason: ChangeReason): void {
    for (const l of this.listeners) l(this.state, reason);
  }
}
